import { memo } from 'react';
import { parseBriefing } from '@nabu/shared/briefing';
import { useI18n, type CopyKey } from '../i18n';
import type { ChatMessage } from '../api';
import { bubbleKind, carriesSubject, outcomeOf, type BubbleKind } from './kinds';

/**
 * One message, and whatever the log knows about where it came from.
 *
 * **Memoised, and that is a performance decision with a name.** The thread is refetched
 * whole every twenty seconds — deliberately, see `refresh` in `Chat.tsx` — so every poll
 * hands React a new array of new objects. Without this, a learner three weeks in with two
 * hundred bubbles on screen re-renders two hundred components three times a minute for a
 * conversation that changed by nothing, which on a mid-range Android is a visible hitch
 * every twenty seconds and a battery cost on a screen people leave open. `merge` in
 * `Chat.tsx` is the other half: it returns the *same* message objects when nothing changed,
 * so the default shallow comparison here actually holds.
 *
 * The marker emoji stays in the text. That was decided in F-100 and it is still right: the
 * learner has been reading `📖` and `❓` in notifications and in this thread for weeks, and a
 * screen that stripped
 * them would be showing a different message from the one on their phone. The label above
 * the bubble names the kind in words *as well*, for the same reason the markers exist — so
 * a briefing can be told from a question without reading either.
 */

const LABELS: Record<BubbleKind, CopyKey> = {
  said: 'chat.kind.said',
  briefing: 'chat.kind.briefing',
  question: 'chat.kind.question',
  reminder: 'chat.kind.reminder',
  mark: 'chat.kind.mark',
  reply: 'chat.kind.reply',
  summary: 'chat.kind.summary',
  notice: 'chat.kind.notice',
};

const OUTCOME_LABELS: Record<string, CopyKey> = {
  wrong: 'chat.mark.wrong',
  partial: 'chat.mark.partial',
  correct: 'chat.mark.correct',
  effortless: 'chat.mark.effortless',
};

/**
 * The time on the bubble, or nothing at all.
 *
 * A message with no readable timestamp is stored under the literal word `unknown` —
 * deliberately, so a redelivery lands on the same row — so this has to answer for a string
 * that is not a date. Showing nothing is right: an invented time on a message we cannot
 * place is worse than no time, and `Invalid Date` printed at a learner is worse than both.
 */
export function clock(at: string, locale: string): string {
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * The message's own text, as paragraphs and lists rather than as one block.
 *
 * Leo, 2026-09-16: *"briefings are not quite friendly to read, theyre just a block of text
 * … mine for example list a bunch of points in a single line, that makes it really hard to
 * read and would be much better as a bulleted list."* The rendering half of that. The other
 * half is the generation prompt, which used to forbid structure outright and is why no
 * amount of work here would have been enough on its own — see `@nabu/shared/briefing`.
 *
 * **Nothing here interprets markup, and that is a constraint rather than an economy.** A
 * briefing is model output written from documents the *learner* supplied, so a PDF is an
 * injection surface and this is the one screen its text reaches. `parseBriefing` returns
 * strings; React puts strings in text nodes; there is no `dangerouslySetInnerHTML` and no
 * Markdown engine on the path. A briefing containing `<script>` renders as the characters
 * `<script>`. `marked` and `markdown-it` were the obvious alternative and both pass raw HTML
 * through by default, which is the whole hole, and both cost tens of kilobytes on a phone
 * for three constructs.
 *
 * `pre-wrap` stays on the paragraphs — see `chat.css`. It is doing a different job now that
 * blocks exist and still a necessary one: a multiple-choice question's options are one per
 * line *inside* a paragraph (`questionText` in `packages/messaging/src/choice.ts`), and the
 * numbers are what the learner replies with, so those line breaks are load-bearing.
 */
export function Prose({ text }: { text: string }) {
  const blocks = parseBriefing(text);
  return (
    <>
      {blocks.map((block, index) => {
        // Index keys, which is safe here and nowhere else in this file: a message's text
        // never changes, so this list is never reordered, inserted into or filtered.
        if (block.kind === 'bullets') {
          return (
            <ul className="chat-msg-list" key={index}>
              {block.items.map((item, at) => (
                <li className="chat-msg-item" key={at}>
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        if (block.kind === 'heading') {
          return (
            <h3 className="chat-msg-head" key={index}>
              {block.text}
            </h3>
          );
        }
        return (
          <p className="chat-msg-text" key={index}>
            {block.text}
          </p>
        );
      })}
    </>
  );
}

export interface BubbleProps {
  message: ChatMessage;
  /** The subject's name, already resolved and localised. Absent when it is not known. */
  subject?: string | undefined;
  /** Opens the sheet that says where this message came from. */
  onSubject?: ((message: ChatMessage) => void) | undefined;
}

function Row({ message, subject, onSubject }: BubbleProps) {
  const { t, locale } = useI18n();
  const kind = bubbleKind(message);
  const outcome = outcomeOf(message.purpose);
  const mine = kind === 'said';
  const label = outcome ? OUTCOME_LABELS[outcome] : LABELS[kind];

  return (
    <article className={`chat-msg chat-msg-${kind}${outcome ? ` chat-mark-${outcome}` : ''}`}>
      {/* The kind, in words, above the bubble rather than inside it: inside, a one-word
          label reads as the first line of the message, which on a briefing is exactly the
          line somebody skims. Hidden from a screen reader for the learner's own messages,
          where "You said" is already announced by the bubble's own label below. */}
      <p className="chat-msg-kind">{t(label ?? LABELS[kind])}</p>

      <div className="chat-msg-body">
        {/* Real paragraphs and real lists, for every kind of message and not only briefings:
            the parser is total, a message with no structure in it comes back as the one
            paragraph it already was, and a rule that applied to briefings alone would need
            `purpose` to be present — which it is not on any row written before F-100. */}
        <Prose text={message.text} />
      </div>

      <p className="chat-msg-foot">
        <span className="chat-sr">{mine ? t('chat.said.you') : t('chat.said.nabu')}</span>
        {/*
         * The provenance chip, and it is a button because it is the tap target.
         *
         * Making the whole bubble tappable was the obvious alternative and is worse twice
         * over: it takes text selection away from the one screen where somebody wants to
         * copy a fact out, and it hides the affordance entirely — a learner has no way to
         * know which messages have something behind them and which do not. The chip is
         * visible, it already answers "what subject was this?" without being tapped, and
         * the tap adds the progress and, since F-116, what the material was written from.
         */}
        {/*
         * Offered when there is a subject to name **or** an item whose sources can be
         * resolved, which is wider than it was and deliberately so. The chip used to require
         * a subject, so a briefing from a closed objective — or one whose node has since
         * been renumbered out of the progress read — carried no chip at all, and the sheet's
         * own `chat.subject.gone` copy was written for a state nothing could reach. Those are
         * exactly the old briefings somebody scrolls back to and asks where it came from.
         */}
        {carriesSubject(kind) &&
          onSubject !== undefined &&
          (subject !== undefined || message.itemId !== undefined) && (
            <button type="button" className="chat-chip-subject" onClick={() => onSubject(message)}>
              {subject ?? t('chat.src.chip')}
            </button>
          )}
        <span className="chat-msg-time">{clock(message.at, locale)}</span>
      </p>
    </article>
  );
}

export default memo(Row);
